/**
 * Product catalogue. One entry renders one page at /products/<slug>/ and one
 * card in the shop.
 *
 * `status` controls what visitors see:
 *  - "coming-soon": the page shows what's planned and a notify-me form. No
 *    price, no buy button.
 *  - "live": the page shows screenshots, licence tiers with buy buttons, FAQ,
 *    and the refund note. Set each licence's `checkoutUrl` first.
 *
 * Prices, contents, and FAQ below are PLACEHOLDERS until each product is
 * built. Replace them with real facts before setting a product to "live".
 */

export type ProductStatus = "coming-soon" | "live";

export type Licence = {
  id: "personal" | "commercial";
  name: string;
  price: number;
  summary: string;
  /** Merchant-of-record checkout link (Lemon Squeezy / Polar). Null = not live. */
  checkoutUrl: string | null;
};

export type Screenshot = {
  label: string;
  /** Pastel surface token used by the placeholder tile. */
  tone: "mist" | "butter" | "blush" | "mint";
};

export type Audience = "developers" | "creators";

export const audienceLabels: Record<Audience, string> = {
  developers: "For developers",
  creators: "For creators",
};

export type Product = {
  slug: string;
  name: string;
  /** Who it's for. Drives the shop filter. */
  audience: Audience;
  /** Pastel surface used for the product's card in the shop. */
  tone: Screenshot["tone"];
  tagline: string;
  /** Short line shown on cards and in search results. */
  description: string;
  status: ProductStatus;
  /** Live demo URL. Null = shows a "coming soon" pill. Never sell code without one. */
  demoUrl: string | null;
  techStack: string[];
  includes: string[];
  screenshots: Screenshot[];
  licences: Licence[];
  faq: { q: string; a: string }[];
};

/**
 * Add real products here, one entry per product. See lib/products.example.ts
 * for a filled-in example to copy from.
 *
 * When you add the FIRST product, also move
 * components/site/product/route.template.tsx to
 * app/products/[slug]/page.tsx so its page is built (a static export can't
 * build the route while this list is empty).
 */
export const products: Product[] = [];


/** The cheapest licence price, for "From $X" labels. */
export function fromPrice(product: Product): number {
  return Math.min(...product.licences.map((l) => l.price));
}

export function getProduct(slug: string): Product | undefined {
  return products.find((p) => p.slug === slug);
}
